import React from 'react';
import { Link } from 'react-router-dom';
import style from './index.module.css';
import Card from '../../components/Card';
import DetailsCard from '../../components/DetailsCard';
import PlanMarry from '../../components/PlanMarry';
import MyFooter from '../../components/MyFooter';
import AboutUsSection from '../../components/AboutUsSection';
import Counter from '../../components/Counter';
import { counterList, eventList, detailsList, testimonialList } from '../../assets/constants';
import { getImage } from '../../utils/utils';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';

const getServices = () => {
	var services = [];
	for (var i = 0; i < 6; i++) {
		services.push(eventList[i]);
	}
	return services;
};

const fadeInUp = {
	initial: { opacity: 0, y: 60 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.6 }
};

const stagger = {
	animate: {
		transition: {
			staggerChildren: 0.1
		}
	}
};

const Home = ({ eventCount }) => {
	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';

	// Integrate eventCount from props if it exists
	const displayCounterList = counterList.map((item, index) => {
		if (index === 1 && eventCount !== undefined) {
			return { ...item, count: eventCount };
		}
		return item;
	});

	return (
		<motion.div
			initial="initial"
			animate="animate"
			exit={{ opacity: 0 }}
			style={{
				width: '100%',
				minHeight: '100vh',
				backgroundColor: theme.palette.background.default,
				color: theme.palette.text.primary,
			}}
		>
			<div className={style.heroSection}>
				<motion.div 
					variants={fadeInUp}
					className={style.heroContent}
					style={{
						background: isDark 
							? 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)' 
							: 'linear-gradient(135deg, #4e2f7f 0%, #7c4dff 100%)',
					}}
				>
					<motion.h1 
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.8 }}
					>
						Shri Hari Tent House & Wedding Planner
					</motion.h1>
					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.5 }}
					>
						Turning your dreams into grand celebrations
					</motion.p>
				</motion.div>
				<motion.img
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 1 }}
					style={{ width: '100%', maxHeight: '70vh', objectFit: 'cover' }}
					src={getImage('image0')}
					alt='Hero'
				/>
			</div>

			{/* Services List */}
			<motion.div 
				variants={stagger}
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.2 }}
				className={style.section}
			>
				<motion.div variants={fadeInUp} className={style.sectionHeader}>
					<p style={{ color: theme.palette.primary.main }}>WHAT WE DO</p>
					<h1>Our Services</h1>
				</motion.div>
				<div className={style.eventListStyle}>
					{getServices().map((item, index) => {
						return (
							<motion.div key={index} variants={fadeInUp}>
								<Card
									header={item.header}
									content={item.content}
									icon={item.icon}
								/>
							</motion.div>
						);
					})}
				</div>
				<motion.div variants={fadeInUp} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ textAlign: 'center', marginTop: '3rem' }}>
					<Link className={style.link} to={'/our_services'}>
						{'View All Services'}
					</Link>
				</motion.div>
			</motion.div>

			{/* Welcome section */}
			<motion.div 
				variants={fadeInUp}
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.3 }}
				style={{ margin: '4rem auto' }}
			>
				<AboutUsSection applyStyle={true} showMore={true} />
			</motion.div>

			{/* Details List */}
			<div className={style.detailsSection} style={{ backgroundColor: isDark ? '#121212' : '#f9f9f9' }}>
				<motion.div 
					variants={stagger}
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, amount: 0.2 }}
					className={style.detailsList}
				>
					{detailsList.map((item, index) => {
						return (
							<motion.div key={index} variants={fadeInUp}>
								<DetailsCard
									header={item.header}
									content={item.content}
									image={item.image}
								/>
							</motion.div>
						);
					})}
				</motion.div>
			</div>

			{/* Wedding Decor & Tenting */}
			<motion.div 
				variants={fadeInUp}
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.3 }}
				className={style.weddingDecorSection}
				style={{ backgroundColor: theme.palette.primary.main }}
			>
				<motion.h1 variants={fadeInUp} style={{ marginBottom: '3rem' }}>Wedding Decor & Tenting</motion.h1>
				<div className={style.weddingDecorImageSection}>
					{[2, 3, 4].map((num) => (
						<motion.img
							key={num}
							variants={fadeInUp}
							whileHover={{ scale: 1.05, rotate: 2 }}
							className={style.weddingDecorImageStyle}
							src={getImage(`image${num}`)}
							alt={`Decor ${num}`}
						/>
					))}
				</div>
			</motion.div>

			{/* Performance Counters */}
			<div
				className={style.section}
				style={{
					backgroundColor: isDark ? 'rgba(124, 77, 255, 0.05)' : 'rgba(78, 47, 127, 0.05)',
					paddingBlock: '5rem',
				}}
			>
				<motion.div 
					variants={stagger}
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, amount: 0.3 }}
				>
					<motion.div variants={fadeInUp} className={style.sectionHeader}>
						<p style={{ color: theme.palette.primary.main }}>OUR ACHIEVEMENTS</p>
						<h1>Our Performance</h1>
					</motion.div>
					<motion.div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexWrap: 'wrap',
							gap: '2rem'
						}}
					>
						{displayCounterList.map((item, index) => {
							return (
								<motion.div key={index} variants={fadeInUp}>
									<Counter
										firstImage={item.firstImage}
										secondImage={item.secondImage}
										count={item.count}
										content={item.content}
										suffix={item.suffix}
									/>
								</motion.div>
							);
						})}
					</motion.div>
				</motion.div>
			</div>

			{/* Testimonials */}
			<div className={style.testimonialSection}>
				<div className={style.sectionHeader}>
					<p style={{ color: theme.palette.primary.main }}>TESTIMONIALS</p>
					<h1>What Our Clients Say</h1>
				</div>
				<motion.div 
					variants={stagger}
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, amount: 0.2 }}
					className={style.testimonialGrid}
				>
					{testimonialList.map((item, index) => (
						<motion.div 
							key={index} 
							variants={fadeInUp}
							className={style.testimonialCard}
							style={{
								backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
								boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.05)',
							}}
						>
							<FormatQuoteIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.5 }} />
							<p className={style.quote}>"{item.quote}"</p>
							<div className={style.rating}>
								{[...Array(item.rating)].map((_, i) => (
									<StarIcon key={i} sx={{ color: '#ffc107', fontSize: 20 }} />
								))}
							</div>
							<div className={style.clientInfo}>
								<h3>{item.name}</h3>
								<p>{item.sign} | {item.from}</p>
							</div>
						</motion.div>
					))}
				</motion.div>
			</div>

			{/* Plan a Marriage */}
			<motion.div
				initial={{ scale: 0.9, opacity: 0 }}
				whileInView={{ scale: 1, opacity: 1 }}
				viewport={{ once: true }}
			>
				<PlanMarry />
			</motion.div>

			{/* Footer Section */}
			<MyFooter />
		</motion.div>
	);
};

export default Home;
